/**
 * Client for the .NET Host's cron-override endpoints (Cron Jobs redesign,
 * Phase 2). The Admin app talks to Supabase for everything else, but committing
 * a schedule change must ALSO live-reschedule Hangfire — which only the Host can
 * do — so commit + conflict-preview go through these endpoints. We forward the
 * same Supabase access token the PostgREST clients use; the Host validates it.
 */
import { supabase } from '@/lib/supabase';

const BASE = import.meta.env.VITE_HOST_API_URL?.replace(/\/+$/, '') ?? '';

/** False when VITE_HOST_API_URL is unset — callers should disable commit UI. */
export const HOST_API_CONFIGURED = Boolean(BASE);

export interface CronChange {
  jobId: string;
  cronExpression: string;
  queue?: string | null;
  enabled: boolean;
  reason?: string | null;
}

export interface QueueConflictSummary {
  pairCount: number;
  byQueue: Record<string, number[]>;
}

export interface JobPreview {
  jobId: string;
  queue: string;
  effectiveCronBefore: string;
  effectiveCronAfter: string;
  conflictMinutesBefore: number[];
  conflictMinutesAfter: number[];
}

export interface InvalidChange {
  jobId: string;
  cronExpression: string;
  error: string;
}

export interface PreviewResult {
  before: QueueConflictSummary;
  after: QueueConflictSummary;
  perJob: JobPreview[];
  invalid: InvalidChange[];
}

export interface AppliedJob {
  jobId: string;
  effectiveCron: string;
  queue: string;
  enabled: boolean;
}

export interface CommitResult {
  applied: AppliedJob[];
  after: QueueConflictSummary;
}

async function authHeaders(): Promise<HeadersInit> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function post<T>(path: string, body: unknown): Promise<T> {
  if (!BASE) {
    throw new Error('Host API URL is not configured. Set VITE_HOST_API_URL to enable schedule commits.');
  }
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { error?: unknown };
      if (typeof parsed?.error === 'string') detail = parsed.error;
    } catch {
      // non-JSON error body — keep the status line.
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

export function previewCronChanges(changes: CronChange[]): Promise<PreviewResult> {
  return post<PreviewResult>('/api/cron-overrides/preview', { changes });
}

export function commitCronChanges(changes: CronChange[]): Promise<CommitResult> {
  return post<CommitResult>('/api/cron-overrides/batch', { changes });
}

export interface SeasonCompletenessRunResult {
  enqueued: { genius: string; presto: string };
}

/** Enqueues the genius + presto completeness audit jobs on the Host (Hangfire). */
export function runSeasonCompletenessAudit(): Promise<SeasonCompletenessRunResult> {
  return post<SeasonCompletenessRunResult>('/api/season-completeness/run', {});
}

async function get<T>(path: string): Promise<T> {
  if (!BASE) {
    throw new Error('Host API URL is not configured. Set VITE_HOST_API_URL to enable schedule tools.');
  }
  const res = await fetch(`${BASE}${path}`, { method: 'GET', headers: await authHeaders() });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const parsed = (await res.json()) as { error?: unknown };
      if (typeof parsed?.error === 'string') detail = parsed.error;
    } catch {
      // non-JSON error body — keep the status line.
    }
    throw new Error(detail);
  }
  return (await res.json()) as T;
}

// --- Schedule optimizer + evaluation + versioning (Cron Jobs, issue #148) ---

export interface CatalogJob {
  jobId: string;
  queue: string;
  defaultCron: string;
  effectiveCron: string;
  enabled: boolean;
  overridden: boolean;
  optimizable: boolean;
}

export interface QueueLoad {
  queue: string;
  peakConcurrency: number;
  capacity: number;
  overCapacity: boolean;
  conflictMinutes: number[];
  spreadScore: number;
}

export interface OrderingViolation {
  syncJobId: string;
  harvestJobId: string;
  syncMinute: number;
  harvestMinute: number;
}

export interface ScheduleEvaluation {
  totalConflictPairs: number;
  queues: QueueLoad[];
  orderingViolations: OrderingViolation[];
}

export interface OptimizeProposedChange {
  jobId: string;
  cronExpression: string;
  queue: string | null;
  enabled: boolean;
  reason: string | null;
}

export interface OptimizeResult {
  proposed: OptimizeProposedChange[];
  before: ScheduleEvaluation;
  after: ScheduleEvaluation;
}

export interface VersionJobChange {
  jobId: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE';
  oldCron: string | null;
  newCron: string | null;
}

export interface ScheduleVersion {
  txid: number;
  changedAt: string;
  changedBy: string | null;
  reason: string | null;
  changes: VersionJobChange[];
}

export type ScheduleScope = 'genius' | 'presto' | 'both';

export function fetchCatalog(): Promise<CatalogJob[]> {
  return get<CatalogJob[]>('/api/cron-overrides/catalog');
}

export function evaluateSchedule(): Promise<ScheduleEvaluation> {
  return get<ScheduleEvaluation>('/api/cron-overrides/evaluate');
}

export function optimizeSchedule(scope: ScheduleScope): Promise<OptimizeResult> {
  return post<OptimizeResult>('/api/cron-overrides/optimize', { scope });
}

export function fetchScheduleVersions(limit = 50): Promise<ScheduleVersion[]> {
  return get<ScheduleVersion[]>(`/api/cron-overrides/versions?limit=${limit}`);
}

export function restoreScheduleVersion(txid: number): Promise<CommitResult> {
  return post<CommitResult>(`/api/cron-overrides/versions/${txid}/restore`, {});
}
